import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getJobs, getCategories, updateJobStatus, deleteJob } from '../../../services/admin/jobService';
import { contractTypes, workModes, getContractTypeLabel, getWorkModeLabel, getStatusLabel } from '../../../utils/jobConstants';
import { formatDate, formatRelativeTime, formatDateWithRelative } from '../../../utils/dateFormatter';
import { toast } from 'react-toastify';

const JobsList = () => {
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  // Filters state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [contractTypeFilter, setContractTypeFilter] = useState('');
  const [isFeaturedFilter, setIsFeaturedFilter] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [emptyFields, setEmptyFields] = useState('');
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrev: false
  });
  
  const limit = 12; // Max records per page

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await getCategories();
        if (response.success && response.data) {
          setCategories(response.data);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };
    
    fetchCategories();
  }, []);

  // Fetch jobs with filters
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = {
        page: pagination.currentPage,
        limit,
        sortBy,
        sortOrder,
        emptyFields
      };
      
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (contractTypeFilter) params.contractType = contractTypeFilter;
      if (isFeaturedFilter !== '') params.isFeatured = isFeaturedFilter === 'true';
      
      const response = await getJobs(params);
      
      if (response.success) {
        setJobs(response.data || []);
        setPagination(response.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          hasNext: false,
          hasPrev: false
        });
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, categoryFilter, contractTypeFilter, isFeaturedFilter, sortBy, sortOrder, emptyFields, pagination.currentPage]);

  // Fetch jobs when filters change
  useEffect(() => {
    // Reset to page 1 when filters change
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [searchTerm, statusFilter, categoryFilter, contractTypeFilter, isFeaturedFilter, sortBy, sortOrder, emptyFields]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Handle search with debounce
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    switch (filterName) {
      case 'status':
        setStatusFilter(value);
        break;
      case 'category':
        setCategoryFilter(value);
        break;
      case 'contractType':
        setContractTypeFilter(value);
        break;
      case 'isFeatured':
        setIsFeaturedFilter(value);
        break;
      case 'sortBy':
        setSortBy(value);
        break;
      case 'sortOrder':
        setSortOrder(value);
        break;
      case 'emptyFields':
        setEmptyFields(value);
        break;
      default:
        break;
    }
  };

  // Handle pagination
  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: page }));
    }
  };

  // Handle job status update
  const handleStatusUpdate = async (jobId, newStatus) => {
    try {
      const response = await updateJobStatus(jobId, newStatus);
      if (response.success) {
        //fetchJobs(); // Refresh the list
        toast.success(`Status updated to ${newStatus} successfully`);
         
        setJobs(jobs.map(job => 
          job._id === jobId ? { ...job, status: newStatus } : job
        ));
      }      
    } catch (error) {
      console.error('Error updating job status:', error);
    }
  };

  // Handle job deletion
  const handleDeleteJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        // You'll need to implement deleteJob in jobService
        const response = await deleteJob(jobId);
        if (response.success) {
          // fetchJobs(); // Refresh the list
          toast.success(response.message || 'Job deleted successfully');

          // For now, just update the local state
          setJobs(jobs.filter(job => job._id !== jobId));
        }
        
        
      } catch (error) {
        console.error('Error deleting job:', error);
      }
    }
  };

  // Helper functions
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactive' },
      closed: { color: 'bg-red-100 text-red-800', label: 'Closed' }
    };
    const config = statusConfig[status] || statusConfig.inactive;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{getStatusLabel(status)}</span>;
  };

  const getContractTypeBadge = (type) => {
    const typeConfig = {
      'Full-time': { color: 'bg-blue-100 text-blue-800', label: 'Full Time' },
      'Part-time': { color: 'bg-purple-100 text-purple-800', label: 'Part Time' },
      'Contract': { color: 'bg-orange-100 text-orange-800', label: 'Contract' },
      'Permanent': { color: 'bg-indigo-100 text-indigo-800', label: 'Permanent' },
      'Internship': { color: 'bg-pink-100 text-pink-800', label: 'Internship' },
      'Freelance': { color: 'bg-teal-100 text-teal-800', label: 'Freelance' }
    };
    const config = typeConfig[type] || typeConfig['Full-time'];
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{getContractTypeLabel(type)}</span>;
  };

  const getWorkModeBadge = (mode) => {
    const modeConfig = {
      'On-site': { color: 'bg-gray-100 text-gray-800', label: 'On Site' },
      'Remote': { color: 'bg-green-100 text-green-800', label: 'Remote' },
      'Hybrid': { color: 'bg-blue-100 text-blue-800', label: 'Hybrid' }
    };
    const config = modeConfig[mode] || modeConfig['On-site'];
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{getWorkModeLabel(mode)}</span>;
  };

  const formatSalary = (salary) => {
    if (!salary || (salary.min === 0 && salary.max === 0)) {
      return 'Not specified';
    }
    
    const currencySymbols = {
      USD: '$',
      INR: '₹',
      EUR: '€',
      GBP: '£'
    };
    
    const symbol = currencySymbols[salary.currency] || salary.currency;
    
    if (salary.min === salary.max) {
      return `${symbol}${salary.min.toLocaleString()}`;
    }
    
    return `${symbol}${salary.min.toLocaleString()} - ${symbol}${salary.max.toLocaleString()}`;
  };

  const formatExperience = (experience) => {
    if (!experience || experience.min === 0 && experience.max === 0) {
      return 'Not specified';
    }
    
    if (experience.min === experience.max) {
      return `${experience.min} ${experience.unit}`;
    }
    
    return `${experience.min} - ${experience.max} ${experience.unit}`;
  };

  const formatDateForDisplay = (dateString) => {
    try {
      return formatDate(dateString, 'datetime');
    } catch (error) {
      return dateString;
    }
  };

  // Generate pagination items
  const generatePaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    const { currentPage, totalPages } = pagination;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      items.push(i);
    }
    
    return items;
  };

  return (
    <div>
      {/* SEO Settings */}
      <title>{`Jobs Management | ${process.env.REACT_APP_NAME}`}</title>
      <meta name="robots" content="noindex, nofollow" />

      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-white">Jobs Management</h2>
            <p className="text-gray-400">
              {loading ? 'Loading jobs...' : `Total ${pagination.totalItems} jobs found`}
            </p>
          </div>
          <Link
            to="/hpanel/jobs/add"
            className="btn-primary text-white font-semibold py-2 px-4 rounded-lg flex items-center"
          >
            <i className="fas fa-plus mr-2"></i>
            Post New Job
          </Link>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search jobs by title, company, or location..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i className="fas fa-search text-gray-400"></i>
              </div>
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <select
              value={categoryFilter}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={loadingCategories}
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category._id} value={category._id}>{category.categoryName || category.name}</option>
              ))}
            </select>
          </div>

          {/* Contract Type Filter */}
          <div>
            <select
              value={contractTypeFilter}
              onChange={(e) => handleFilterChange('contractType', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Types</option>
              {contractTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          {/* Featured Filter */}
          <div>
            <select
              value={isFeaturedFilter}
              onChange={(e) => handleFilterChange('isFeatured', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">All Jobs</option>
              <option value="true">Featured Only</option>
              <option value="false">Non-Featured</option>
            </select>
          </div>

          {/* Sort Order */}
          <div>
            <select
              value={sortOrder}
              onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>

          {/* Redirect URL / Apply Email */}
          <div>
            <select
              value={emptyFields}
              onChange={(e) => handleFilterChange('emptyFields', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Empty Fields</option>
              <option value="redirectUrl">Redirect URL</option>
              <option value="applyEmail">Apply Email</option>
              <option value="both">Both</option>
            </select>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
          <p className="mt-4 text-gray-600">Loading jobs...</p>
        </div>
      )}

      {/* Jobs Grid */}
      {!loading && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {jobs.map((job) => (
              <div key={job._id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">{job.title}</h3>

                      <p className="text-sm text-gray-600 truncate">
                        <i className="fas fa-building mr-1"></i> {job.company}
                      </p>
                    </div>

                    <div className="text-right ml-4">
                      <div className="mb-2">{getStatusBadge(job.status)}</div>
                      <div className="text-xs text-gray-500">
                        {job.isFeatured && (
                          <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full mr-1">
                            Featured
                          </span>
                        )}
                        {job.isUrgentHiring && (
                          <span className="inline-block px-2 py-1 bg-red-100 text-red-800 rounded-full">
                            Urgent
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  {/* <p className="text-sm text-gray-600 mb-4 line-clamp-2">{job.description}</p> */}

                  {/* Category */}
                  <div className="mb-4">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {job.category?.categoryName || job.category?.name || 'Uncategorized'}
                    </span>

                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ml-2">
                      <i className="fas fa-map-marker mr-1"></i> {job.location?.displayName || 'Location not specified'}
                    </span>
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {getContractTypeBadge(job.contractType)}
                    {getWorkModeBadge(job.workMode)}
                  </div>

                  {/* Skills */}
                  {/* {job.skills && job.skills.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {job.skills.slice(0, 3).map((skill, index) => (
                        <span key={index} className="px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                          {skill}
                        </span>
                      ))}
                      {job.skills.length > 3 && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          +{job.skills.length - 3} more
                        </span>
                      )}
                    </div>
                  )} */}

                  {/* Details */}
                  <div className="space-y-2 mb-4 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>Salary:</span>
                      <span className="font-medium text-gray-800">{formatSalary(job.salary)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Experience:</span>
                      <span className="font-medium text-gray-800">{formatExperience(job.experience)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Posted:</span>
                      <span className="font-medium text-gray-800">{formatDateForDisplay(job.createdAt)}</span>
                    </div>
                  </div>

                  {/* Stats */}
                  {/* <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="text-center">
                      <div className="text-lg font-semibold text-gray-800">{job.applications || 0}</div>
                      <div className="text-xs text-gray-500">Applications</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-gray-800">{job.clicks || 0}</div>
                      <div className="text-xs text-gray-500">Clicks</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-semibold text-gray-800">{job.views || 0}</div>
                      <div className="text-xs text-gray-500">Views</div>
                    </div>
                  </div> */}

                  {/* Actions */}
                  <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                    <div className="flex space-x-2">
                      <Link
                        to="#"
                        onClick={() => window.open(`/jobs/${job._id}`, '_blank')}
                        className="text-primary-600 text-sm font-medium bg-blue-200 px-2 py-1 rounded-md inline-flex items-center"
                      >
                        <i className="fas fa-eye mr-1"></i> View
                      </Link>
                      <Link
                        to={`/hpanel/jobs/edit/${job._id}`}
                        className="text-green-800 text-sm font-medium bg-green-200 px-2 py-1 rounded-md inline-flex items-center"
                      >
                        <i className="fas fa-edit mr-1"></i> Edit
                      </Link>

                      <button
                        onClick={() => handleStatusUpdate(job._id, job.status === 'active' ? 'inactive' : 'active')}
                        className={`text-sm font-medium px-2 py-1 rounded-md inline-flex items-center ${job.status === 'active' ? 'text-orange-800 bg-orange-200' : 'text-blue-800 bg-blue-200'}`}
                      >
                        <i className={`fas ${job.status === 'active' ? 'fa-pause' : 'fa-play'} mr-1`}></i>
                        {job.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>

                    <button
                      onClick={() => handleDeleteJob(job._id)}
                      className="text-red-800 text-sm font-medium bg-red-200 px-2 py-1 rounded-md inline-flex items-center"
                    >
                      <i className="fas fa-trash mr-1"></i> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {jobs.length === 0 && !loading && (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <i className="fas fa-briefcase text-4xl text-gray-300 mb-4"></i>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No jobs found</h3>
              <p className="text-gray-600 mb-4">Try adjusting your search criteria or create a new job posting.</p>
              <Link
                to="/hpanel/jobs/add"
                className="btn-primary text-white font-semibold py-2 px-6 rounded-lg inline-flex items-center"
              >
                <i className="fas fa-plus mr-2"></i>
                Post New Job
              </Link>
            </div>
          )}

          {/* Pagination */}
          {jobs.length > 0 && pagination.totalPages > 1 && (
            <div className="bg-white rounded-xl shadow-sm px-6 py-4 mt-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(pagination.currentPage - 1) * limit + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.currentPage * limit, pagination.totalItems)}
                  </span> of{' '}
                  <span className="font-medium">{pagination.totalItems}</span> jobs
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrev}
                    className={`px-3 py-1 border border-gray-300 rounded-md text-sm font-medium ${
                      pagination.hasPrev
                        ? 'text-gray-700 hover:bg-gray-50'
                        : 'text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Previous
                  </button>
                  
                  {generatePaginationItems().map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-1 border rounded-md text-sm font-medium ${
                        page === pagination.currentPage
                          ? 'bg-primary-600 text-gray-800 border-primary-600'
                          : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNext}
                    className={`px-3 py-1 border border-gray-300 rounded-md text-sm font-medium ${
                      pagination.hasNext
                        ? 'text-gray-700 hover:bg-gray-50'
                        : 'text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default JobsList;